import React, { useEffect, useRef } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import {
  Canvas as FabricCanvas,
  Text as FabricText,
  Rect as FabricRect,
  Group as FabricGroup,
  Image as FabricImage,
  Point,
  Shadow as FabricShadow,
} from 'fabric';
import { Close } from '@mui/icons-material';
import { PlacedProperty } from './TempImagePropertyMapper';

interface ImageCanvasProps {
  uploadedImage: string | null;
  placedProperties: PlacedProperty[];
  onPropertyDrop: (propertyKey: string, x: number, y: number) => void;
  onPlacedPropertyRemove: (propertyKey: string) => void;
  onPropertyMove?: (propertyKey: string, x: number, y: number) => void;
  isDragMode?: boolean;
}

const TempImageCanvas: React.FC<ImageCanvasProps> = ({
  uploadedImage,
  placedProperties,
  onPropertyDrop,
  onPlacedPropertyRemove,
  onPropertyMove,
  isDragMode = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fabricRef = useRef<FabricCanvas | null>(null);
  const dragPropertyKey = useRef<string | null>(null);
  const CANVAS_WIDTH = 1500;
  const CANVAS_HEIGHT = 800;

  useEffect(() => {
    if (!canvasRef.current) return undefined;
    if (fabricRef.current) {
      fabricRef.current.dispose();
    }
    const canvas = new FabricCanvas(canvasRef.current, {
      width: 1500,
      height: 800,
      backgroundColor: '#fff',
      selection: false,
    });
    fabricRef.current = canvas;
    return () => {
      canvas.dispose();
    };
  }, []);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const propertyKey = e.dataTransfer?.getData('propertyKey');
    if (!propertyKey) return;
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    onPropertyDrop(propertyKey, x, y);
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  useEffect(() => {
    const canvas = fabricRef.current;
    if (!canvas) return;
    canvas.getObjects().forEach((obj) => {
      if (obj.type === 'image') {
        canvas.remove(obj);
      }
    });
    if (uploadedImage) {
      FabricImage.fromURL(uploadedImage, {})
        .then((img) => {
          const scale = Math.min(
            CANVAS_WIDTH / (img.width || 1),
            CANVAS_HEIGHT / (img.height || 1)
          );

          img.set({
            left: 0,
            top: 0,
            scaleX: scale,
            scaleY: scale,
            selectable: false,
            evented: false,
          });
          canvas.add(img);
          canvas.sendObjectToBack(img);
          canvas.renderAll();
        })
        .catch((err) => {
          console.error('Failed to load image in canvas:', err);
        });
    }
  }, [uploadedImage]);

  useEffect((): void | (() => void) => {
    const canvas = fabricRef.current;
    if (!canvas) return undefined;

    canvas.getObjects().forEach((obj) => {
      if (obj.type === 'text' || obj.type === 'group') {
        canvas.remove(obj);
      }
    });

    placedProperties.forEach((property) => {
      if (property.visible === false) return;

      const label = `${property.key}: ${property.value}`;
      const fontSize = 12;
      const fontFamily = 'Inter, sans-serif';
      const fontWeight = 500;
      const paddingX = 12;
      const paddingY = 6;
      const borderRadius = 16;

      const textObj = new FabricText(label, {
        fontSize,
        fontFamily,
        fontWeight,
        fill: '#FFF',
        originX: 'center',
        originY: 'center',
        shadow: new FabricShadow({
          color: 'rgba(0,0,0,0.15)',
          blur: 8,
          offsetX: 0,
          offsetY: 2,
        }),
      });

      const textWidth = textObj.width || 0;
      const textHeight = textObj.height || 0;
      const badgeWidth = textWidth + paddingX * 2;
      const badgeHeight = textHeight + paddingY * 2;
      const rect = new FabricRect({
        width: badgeWidth,
        height: badgeHeight,
        rx: borderRadius,
        ry: borderRadius,
        fill: '#1976d2',
        originX: 'center',
        originY: 'center',
        selectable: false,
        evented: false,
      });
      const badgeGroup = new FabricGroup([rect, textObj], {
        left: property.x,
        top: property.y,
        originX: 'center',
        originY: 'center',
        selectable: isDragMode,
        hasControls: false,
        hasBorders: false,
        lockMovementX: !isDragMode,
        lockMovementY: !isDragMode,
        hoverCursor: isDragMode ? 'move' : 'default',
        scaleX: 1,
        scaleY: 1,
      });
      (badgeGroup as any).propertyKey = property.key;
      canvas.add(badgeGroup);
    });
    canvas.renderAll();

    if (isDragMode && onPropertyMove) {
      const handleObjectMoving = (e: any) => {
        const obj = e.target as FabricText & { propertyKey?: string };
        if (obj && obj.propertyKey) {
          onPropertyMove(obj.propertyKey, obj.left ?? 0, obj.top ?? 0);
        }
      };

      canvas.on('object:moving', handleObjectMoving);

      return () => {
        canvas?.off('object:moving', handleObjectMoving);
      };
    }
    return undefined;
  }, [placedProperties, isDragMode, onPropertyMove]);

  const handleRemove = (propertyKey: string) => {
    onPlacedPropertyRemove(propertyKey);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: 'white',
        overflow: 'auto',
        display: 'block',
        padding: 0,
        margin: 0,
      }}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
    >
      <canvas
        ref={canvasRef}
        style={{ display: 'block', margin: 0, padding: 0 }}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        data-canvas="true"
      />
      {!uploadedImage && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#999',
            flexDirection: 'column',
            zIndex: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            No image uploaded
          </Typography>
          <Typography variant="body2">
            Upload an image to start placing properties
          </Typography>
        </Box>
      )}

      {isDragMode &&
        placedProperties
          .filter((p) => p.visible !== false)
          .map((property) => (
            <IconButton
              key={property.key}
              size="small"
              onClick={() => property.key && handleRemove(property.key)}
              sx={{
                position: 'absolute',
                left: property.x,
                top: property.y - 20,
                zIndex: 10,
                background: '#fff',
                color: 'black',
                width: 20,
                height: 20,
                transform: 'translate(-50%, -50%)',
                '&:hover': {
                  background: 'white',
                  color: 'black',
                },
              }}
            >
              <Close style={{ fontSize: '14px' }} />
            </IconButton>
          ))}
    </Box>
  );
};

export default TempImageCanvas;
